import { Table, Tag, Button } from 'antd';
import { DATE_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatHistoryRow, fetchChatHistory } from 'services/api/chatHistory';

import ChatModal from 'components/shared/AiRequest/ChatModal';

interface IProps {
  limit?: number;
  pagination?: boolean;
  status?: number;
  className?: string;
}

const AiRequest: FC<IProps> = ({
  limit = 10,
  pagination = true,
  status,
  className
}) => {
  const [chatId, setChatId] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || 1);

  const [historyData, setHistoryData] = useState<ChatHistoryRow[]>([]);

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchChatHistory({
          page,
          limit,
          ...(typeof status === 'undefined' ? {} : { finished: !!status })
        });

        if (cancelled) return;
        setHistoryData(res.data?.results ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        // swallow — empty table on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, limit, status]);

  return (
    <>
      <Table
        dataSource={historyData}
        loading={loading}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: id => id
          },
          {
            title: 'Title',
            dataIndex: 'title',
            key: 'title'
          },
          {
            title: 'Email',
            dataIndex: 'user',
            key: 'user',
            render: user =>
              user?.email ? (
                <a href={`mailto:${user.email}`}>{user.email}</a>
              ) : (
                '-'
              )
          },
          {
            title: 'Status',
            dataIndex: 'finished',
            key: 'finished',
            render: value =>
              value ? (
                <Tag color="success">Submitted</Tag>
              ) : (
                <Tag color="orange">Not Submitted</Tag>
              )
          },
          {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: value => dayjs(value).format(DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'id',
            render: id => (
              <Button type="link" onClick={() => setChatId(id)}>
                Show Chat
              </Button>
            )
          }
        ]}
        pagination={
          pagination
            ? {
                total: total || 0,
                current: page,
                onChange: p => {
                  searchParams.set('page', `${p}`);
                  setSearchParams(searchParams);
                },
                showSizeChanger: false
              }
            : false
        }
        className={className}
        rowKey="id"
      />
      {!!chatId && <ChatModal id={chatId} onClose={() => setChatId('')} />}
    </>
  );
};

export default AiRequest;
